import React, { useRef } from 'react';
import { Plus, Paperclip, Trash2 } from 'lucide-react';
import { paymentProofAPI } from '../services/api';
import { authedMediaUrl } from '../utils/media';
import { PAYMENT_MODE_LABEL } from '../constants/payments';

/** File size for the attachment list. Bytes are never the useful unit here. */
const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The transfer screenshot behind a non-cash payment, shared by every form that records one.
 *
 * <p>Rendered only when the chosen method needs proof, so it appears the moment Mobile Wallet
 * or Bank Transfer is picked and disappears again for cash. `existing` are screenshots already
 * attached (edit mode); `files` are ones picked in this session and not yet uploaded.
 */
const PaymentProofUpload = ({
  paymentMode,
  existing = [],
  files = [],
  onFiles,
  error,
  onClearError,
}) => {
  const inputRef = useRef(null);
  const total = existing.length + files.length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <label className="block text-sm font-semibold">
          Payment screenshot <span className="text-red-500">*</span>
          <span className="ml-2 font-normal text-gray-400 text-xs">
            {PAYMENT_MODE_LABEL[paymentMode] || paymentMode} settles outside the system, so the transfer has to be shown
          </span>
        </label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add screenshot
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          if (picked.length) {
            onFiles([...files, ...picked]);
            if (onClearError) onClearError();
          }
          // Cleared so picking the same file again still fires a change.
          e.target.value = '';
        }}
      />
      {total === 0 ? (
        <div
          className={`rounded-xl border border-dashed px-4 py-3 text-sm ${
            error ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 bg-gray-50 text-gray-500'
          }`}
        >
          Attach the transfer confirmation for this payment.
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {existing.map(p => (
            <div key={p.id} className="flex items-center gap-3 px-3 py-2 bg-white">
              <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <a
                href={authedMediaUrl(paymentProofAPI.fileUrl(p.id))}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 truncate text-sm text-emerald-700 hover:underline"
                title={p.originalFileName || 'Payment screenshot'}
              >
                {p.originalFileName || 'Payment screenshot'}
              </a>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(p.sizeBytes)}</span>
            </div>
          ))}
          {files.map((f, i) => (
            <div key={`new-${i}`} className="flex items-center gap-3 px-3 py-2 bg-emerald-50/40">
              <Paperclip className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-sm text-gray-800" title={f.name}>{f.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => onFiles(files.filter((_, k) => k !== i))}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                title="Remove this file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default PaymentProofUpload;
